/**
 * Timer Alarm 倒计时铃声
 * 使用 Web Audio 生成简单蜂鸣声，避免额外资源依赖
 */

class TimerAlarm {
    constructor() {
        this.isEnabled = true;
        this.isStarting = false;
        this.isRinging = false;
        this.audioContext = null;
        this.oscillator = null;
        this.gainNode = null;
        this.pulseIntervalId = null;
        this.stopTimeoutId = null;
        this.callbacks = {
            onStart: null,
            onStop: null
        };
    }

    /**
     * 设置是否开启铃声
     * @param {boolean} enabled - 是否开启
     */
    setEnabled(enabled) {
        this.isEnabled = Boolean(enabled);
        if (!this.isEnabled) {
            this.stop();
        }
    }

    /**
     * 开始铃声
     * @returns {Promise<boolean>} - 是否成功开始
     */
    async start() {
        if (!this.isEnabled || this.isRinging || this.isStarting) {
            return false;
        }

        const audioContext = this._ensureAudioContext();
        if (!audioContext) {
            return false;
        }

        this.isStarting = true;
        try {
            await audioContext.resume();
        } catch (error) {
            // 忽略恢复失败，保持静默
        }

        if (!this.isEnabled || !this.isStarting || audioContext.state !== 'running') {
            this.isStarting = false;
            return false;
        }

        this._createNodes();
        this._startPulse();
        this.stopTimeoutId = window.setTimeout(() => {
            this.stop();
        }, 60000);

        this.isRinging = true;
        this.isStarting = false;
        this._triggerCallback('onStart');
        return true;
    }

    /**
     * 播放短提示音
     * @returns {Promise<boolean>} - 是否成功播放
     */
    async playShortBeep() {
        if (!this.isEnabled || this.isRinging || this.isStarting) {
            return false;
        }

        const audioContext = this._ensureAudioContext();
        if (!audioContext) {
            return false;
        }

        try {
            await audioContext.resume();
        } catch (error) {
            // 忽略恢复失败，保持静默
        }

        if (audioContext.state !== 'running') {
            return false;
        }

        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        const now = audioContext.currentTime;

        oscillator.type = 'sine';
        // 使用与长铃声一致的"叮-咚"音色，但只播放一次
        oscillator.frequency.setValueAtTime(523.25, now);
        gainNode.gain.setValueAtTime(0.0001, now);
        gainNode.gain.linearRampToValueAtTime(0.12, now + 0.03);
        gainNode.gain.exponentialRampToValueAtTime(0.0001, now + 0.18);

        const secondStart = now + 0.22;
        oscillator.frequency.setValueAtTime(659.25, secondStart);
        gainNode.gain.setValueAtTime(0.0001, secondStart);
        gainNode.gain.linearRampToValueAtTime(0.12, secondStart + 0.03);
        gainNode.gain.exponentialRampToValueAtTime(0.0001, secondStart + 0.23);

        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        oscillator.start(now);
        oscillator.stop(now + 0.6);

        oscillator.addEventListener('ended', () => {
            oscillator.disconnect();
            gainNode.disconnect();
        });

        return true;
    }

    /**
     * 停止铃声
     */
    stop() {
        if (!this.isRinging && !this.isStarting) {
            return;
        }

        this.isStarting = false;
        if (!this.isRinging) {
            return;
        }

        this.isRinging = false;
        this._clearTimers();
        this._stopNodes();
        this._triggerCallback('onStop');
    }

    /**
     * 注册回调函数
     */
    on(event, callback) {
        if (this.callbacks.hasOwnProperty(event)) {
            this.callbacks[event] = callback;
        }
    }

    /**
     * 触发回调函数
     */
    _triggerCallback(event) {
        if (this.callbacks[event] && typeof this.callbacks[event] === 'function') {
            this.callbacks[event]();
        }
    }

    /**
     * 初始化 AudioContext
     */
    _ensureAudioContext() {
        if (this.audioContext) {
            return this.audioContext;
        }

        const AudioContextClass = window.AudioContext || window.webkitAudioContext;
        if (!AudioContextClass) {
            return null;
        }

        this.audioContext = new AudioContextClass();
        return this.audioContext;
    }

    /**
     * 创建音频节点
     */
    _createNodes() {
        this._stopNodes();

        if (!this.audioContext) {
            return;
        }

        this.oscillator = this.audioContext.createOscillator();
        this.gainNode = this.audioContext.createGain();

        // 使用更柔和的波形，避免刺耳的提示音
        this.oscillator.type = 'sine';
        this.oscillator.frequency.value = 523.25;
        this.gainNode.gain.value = 0.0001;

        this.oscillator.connect(this.gainNode);
        this.gainNode.connect(this.audioContext.destination);
        this.oscillator.start();
    }

    /**
     * 启动蜂鸣节奏
     */
    _startPulse() {
        if (!this.gainNode || !this.audioContext) {
            return;
        }

        const pulse = () => {
            if (!this.gainNode || !this.audioContext) {
                return;
            }

            const now = this.audioContext.currentTime;
            this.gainNode.gain.cancelScheduledValues(now);
            // 两段轻柔"叮-咚"节奏，减少急促感
            this.oscillator.frequency.setValueAtTime(523.25, now);
            this.gainNode.gain.setValueAtTime(0.0001, now);
            this.gainNode.gain.linearRampToValueAtTime(0.12, now + 0.03);
            this.gainNode.gain.exponentialRampToValueAtTime(0.0001, now + 0.18);

            const secondStart = now + 0.22;
            this.oscillator.frequency.setValueAtTime(659.25, secondStart);
            this.gainNode.gain.setValueAtTime(0.0001, secondStart);
            this.gainNode.gain.linearRampToValueAtTime(0.12, secondStart + 0.03);
            this.gainNode.gain.exponentialRampToValueAtTime(0.0001, secondStart + 0.23);
        };

        pulse();
        this.pulseIntervalId = window.setInterval(pulse, 1600);
    }

    /**
     * 清理计时器
     */
    _clearTimers() {
        if (this.pulseIntervalId) {
            clearInterval(this.pulseIntervalId);
            this.pulseIntervalId = null;
        }

        if (this.stopTimeoutId) {
            clearTimeout(this.stopTimeoutId);
            this.stopTimeoutId = null;
        }
    }

    /**
     * 关闭音频节点
     */
    _stopNodes() {
        if (this.oscillator) {
            try {
                this.oscillator.stop();
            } catch (error) {
                // 忽略重复停止
            }
            this.oscillator.disconnect();
            this.oscillator = null;
        }

        if (this.gainNode) {
            this.gainNode.disconnect();
            this.gainNode = null;
        }
    }

    /**
     * 销毁
     */
    destroy() {
        this.stop();
        this.callbacks = {};
    }
}

// 导出
export default TimerAlarm;
